import { getObjectId } from '@/helpers/object.id.helper';
import { getEntityRefFromFlow } from '@/helpers/entity-ref.helper';
import { resolveFlowControlExpandIdFromCueRow } from '@/helpers/flow-control-playing-segment.helper';
import {
  resolveCurrentCueSheetPointer,
  resolveLiveNextCueTarget,
  resolveNextCuePreviewRow,
} from '@/helpers/resolve-next-cue-preview-row.helper';
import { findSimulationRowIndex, type FlowControlSegmentKind } from '@/helpers/simulation-cue-engine.helper';
import { isInBetweenPlayoutSegmentKind } from '@/helpers/flow-control-in-between-playout.helper';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useFlowControlSimulationCueStore } from '@/state/stores/use-flow-control-simulation-cue-store';
import { entityRoomKeyFromEntityRef, usePerformControlWsStore } from '@/state/player/perform-control-ws.store';
import { liveCueSheetToSimulationRows } from '@/helpers/live-cue-sheet-to-simulation-rows.helper';
import { useLiveContextCueState } from '@/hooks/use-live-context-cue-state';
import { useNextCueRecalculatingStore } from '@/state/player/next-cue-recalculating.store';
import { isJumpNextIndicatorReady } from '@/helpers/resolve-next-cue-recalculating.helper';

/** Expand id for the segment that contains the Next cue preview target. */
export function useFlowControlNextCueSegmentExpandId(): string | null {
  const flowPlan = useFlowStore((s) => s.flowPlan);
  const flow = useFlowStore((s) => s.flow);
  const flowToUse = flowPlan ?? flow;
  const liveRunning = flowToUse?.liveContext?.isRunning ?? false;
  const performMode = liveRunning ? 'play' : 'simulation';

  const simulationRows = useFlowControlSimulationCueStore((s) => s.rows);
  const simulationPointer = useFlowControlSimulationCueStore((s) => s.pointer);
  const simulationOverridePointer = useFlowControlSimulationCueStore((s) => s.overridePointer);
  const simulationSelectedItemIds = useFlowControlSimulationCueStore((s) => s.selectedItemIds);
  const playedReturnPreviousKeys = useFlowControlSimulationCueStore((s) => s.playedReturnPreviousKeys);

  const flowId = flowToUse?.origin ? getObjectId(flowToUse.origin) : flowToUse?.id;
  const entityRef = flowId ? getEntityRefFromFlow(flowId, flowToUse?.liveContext) : undefined;

  const cueState = useLiveContextCueState(entityRef);
  const jumpIndicatorLatch = useNextCueRecalculatingStore((s) =>
    entityRef ? (s.latchByEntityRef[entityRef] ?? null) : null,
  );
  const roomKey = entityRoomKeyFromEntityRef(entityRef ?? '');
  const liveCueSheet = usePerformControlWsStore((s) => (roomKey ? s.cueSheetByRoom[roomKey] : undefined));
  const liveRows = liveCueSheet ? liveCueSheetToSimulationRows(liveCueSheet) : [];

  if (
    performMode === 'play' &&
    jumpIndicatorLatch?.expectedNext &&
    !isJumpNextIndicatorReady(jumpIndicatorLatch, cueState)
  ) {
    return null;
  }

  if (performMode === 'play') {
    const liveNext = resolveLiveNextCueTarget(cueState);
    if (!liveNext?.itemId) {
      return null;
    }
    const index = findSimulationRowIndex(
      liveRows,
      liveNext.itemId,
      { scope: liveNext.scope, slotId: liveNext.slotId, cueItemId: liveNext.cueItemId },
      cueState?.pointer,
    );
    if (index >= 0) {
      const fromRow = resolveFlowControlExpandIdFromCueRow(liveRows[index] ?? null);
      if (fromRow) {
        return fromRow;
      }
    }
    // Projection won but sheet row missing / incomplete — derive expand id from Next scope/slot.
    // Transition/resume often omit nextSlotId; the playing session slot is the in-between anchor.
    // Break playlists use media scope `slot`; operator segment kind is still `break`.
    const scope = liveNext.scope;
    const segmentKind = (scope === 'slot' ? 'break' : (scope ?? 'pre_flow')) as FlowControlSegmentKind;
    const slotId = liveNext.slotId ?? (isInBetweenPlayoutSegmentKind(segmentKind) ? cueState?.activeSlotId : undefined);
    if (isInBetweenPlayoutSegmentKind(segmentKind) && !slotId) {
      return null;
    }
    const stub = {
      itemId: liveNext.itemId,
      scope,
      slotId,
      ordinal: 0,
      segmentKind,
      segmentAnchor:
        slotId && (isInBetweenPlayoutSegmentKind(segmentKind) || segmentKind === 'session' || segmentKind === 'break')
          ? (`slot:${slotId}` as const)
          : segmentKind === 'flow_outro' || segmentKind === 'after_flow'
            ? ('flow_end' as const)
            : ('flow_start' as const),
    };
    return resolveFlowControlExpandIdFromCueRow(stub);
  }

  const currentPointer = resolveCurrentCueSheetPointer(simulationRows, performMode, simulationPointer, cueState, {
    simulationOverridePointer,
  });
  const nextRow = resolveNextCuePreviewRow(simulationRows, currentPointer, {
    cueOverrideActive: simulationOverridePointer != null,
    resumePointer: simulationPointer,
    selectedItemIds: simulationSelectedItemIds,
    flow: flowToUse ?? undefined,
    playedReturnPreviousKeys,
  });
  return resolveFlowControlExpandIdFromCueRow(nextRow);
}
